import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

interface BackgroundMusicPlayerProps {
  active: boolean; // Chỉ phát khi ở mobile view (TikTokFeed)
  volume?: number; // Volume từ 0-1, default 0.10 (10% audio post volume)
  globalPaused?: boolean; // Pause background music when main audio is paused
}

const BackgroundMusicPlayer: React.FC<BackgroundMusicPlayerProps> = ({ 
  active, 
  volume = 0.10,
  globalPaused = false
}) => {
  const [currentTrack, setCurrentTrack] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [isPausedByMainAudio, setIsPausedByMainAudio] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playlistRef = useRef<string[]>([]);
  const resumeTimeoutRef = useRef<number | null>(null);
  const initTimeoutRef = useRef<number | null>(null);
  const autoPlayAttempts = useRef<number>(0);

  // Background music playlist - sử dụng CDN URLs
  const bgmTracks = useMemo(() => [
    'https://cdn.infonium.org/bgm/NeonGalaxy1.mp3',
    'https://cdn.infonium.org/bgm/NeonGalaxy.mp3', 
    'https://cdn.infonium.org/bgm/EthereaHorizon.mp3',
    'https://cdn.infonium.org/bgm/NeonVeins1.mp3',
    'https://cdn.infonium.org/bgm/NeonVeins.mp3',
    'https://cdn.infonium.org/bgm/VoyageIntoTomorrow.mp3',
    'https://cdn.infonium.org/bgm/backgroundmusic.mp3'
  ], []);

  // Shuffle playlist
  const shuffleArray = (array: string[]): string[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Initialize playlist immediately when component becomes active
  useEffect(() => {
    if (active && !isReady) {
      console.log('[BackgroundMusicPlayer] 🎵 Initializing playlist...');
      // Initialize immediately when active, no unnecessary delay
      initTimeoutRef.current = window.setTimeout(() => {
        playlistRef.current = shuffleArray(bgmTracks);
        if (playlistRef.current.length > 0) {
          setCurrentTrack(playlistRef.current[0]);
          setIsReady(true);
          console.log('[BackgroundMusicPlayer] ✅ Playlist ready, track set:', playlistRef.current[0]);
        }
      }, 1000); // Minimal 1 second delay to allow content to initialize
    }

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, [active, isReady, bgmTracks]);

  // ✅ CRITICAL FIX: Mark audio element as background music to prevent GlobalAudioManager from stopping it
  useEffect(() => {
    if (audioRef.current) {
      // Mark this as background music so GlobalAudioManager doesn't stop it
      audioRef.current.setAttribute('data-background-music', 'true');
      audioRef.current.setAttribute('data-continuous-playback', 'true');
      console.log('[BackgroundMusicPlayer] 🎵 Marked audio as background music');
    }
  }, [currentTrack]);

  // Enhanced autoplay attempt - try immediately and on user interaction
  const attemptAutoplay = useCallback(async (reason: string) => {
    if (!audioRef.current || !isReady || !active || globalPaused || isPlaying) {
      return false;
    }

    try {
      autoPlayAttempts.current++;
      console.log(`[BackgroundMusicPlayer] 🎵 Autoplay attempt #${autoPlayAttempts.current} (${reason})`);
      
      await audioRef.current.play();
      setIsPlaying(true);
      console.log(`[BackgroundMusicPlayer] ✅ Autoplay SUCCESS (${reason})`);
      return true;
    } catch (error) {
      console.log(`[BackgroundMusicPlayer] ⚠️ Autoplay failed (${reason}):`, error);
      return false;
    }
  }, [isReady, active, globalPaused, isPlaying]);

  // Listen for user interaction to enable autoplay
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (!userInteracted) {
        setUserInteracted(true);
        console.log('[BackgroundMusicPlayer] 👆 User interaction detected');
        
        // Try to start music immediately after user interaction
        const success = await attemptAutoplay('user interaction');
        if (!success && isReady && currentTrack) {
          // If still failed, keep trying periodically
          const retryInterval = setInterval(async () => {
            const retrySuccess = await attemptAutoplay('retry after interaction');
            if (retrySuccess) {
              clearInterval(retryInterval);
            }
          }, 2000);
          
          // Stop retrying after 10 seconds
          setTimeout(() => clearInterval(retryInterval), 10000);
        }
      }
    };

    // Add listeners for various user interaction types
    const events = ['touchstart', 'click', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [isReady, currentTrack, active, globalPaused, userInteracted, isPlaying, attemptAutoplay]);

  // Play next track in shuffle
  const playNextTrack = useCallback(() => {
    if (playlistRef.current.length === 0) return;
    
    const currentIndex = playlistRef.current.findIndex(track => track === currentTrack);
    let nextIndex = currentIndex + 1;
    
    // If reached end, reshuffle and start from beginning
    if (nextIndex >= playlistRef.current.length) {
      playlistRef.current = shuffleArray(bgmTracks);
      nextIndex = 0;
    }
    
    setCurrentTrack(playlistRef.current[nextIndex]);
  }, [currentTrack, bgmTracks]);

  // Simplified main control logic - handles play/pause based on conditions
  useEffect(() => {
    if (!audioRef.current || !currentTrack || !isReady) return;

    const audio = audioRef.current;
    audio.volume = volume;

    // Clear any pending resume timeout
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }

    if (globalPaused) {
      // Pause background music when main audio is playing
      if (isPlaying && !isPausedByMainAudio) {
        audio.pause();
        setIsPausedByMainAudio(true);
        console.log('[BackgroundMusicPlayer] ⏸️ Paused due to main audio');
      }
    } else if (!active) {
      // Stop when component becomes inactive
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        setIsPausedByMainAudio(false);
        console.log('[BackgroundMusicPlayer] 🛑 Stopped due to inactive');
      }
    } else {
      // Component is active and no global pause
      if (isPausedByMainAudio) {
        // Resume after main audio stops
        resumeTimeoutRef.current = window.setTimeout(async () => {
          if (audioRef.current && !globalPaused && active && isReady) {
            const success = await attemptAutoplay('resume after main audio');
            if (success) {
              setIsPausedByMainAudio(false);
            }
          }
        }, 500); // Shorter delay for better UX
      } else if (!isPlaying) {
        // Try to start playing - both with and without user interaction
        if (userInteracted) {
          // User has interacted, attempt immediate play
          attemptAutoplay('user interacted ready');
        } else {
          // No user interaction yet, try anyway (some browsers allow it)
          attemptAutoplay('no interaction yet');
        }
      }
    }

    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    };
  }, [active, currentTrack, volume, isPlaying, userInteracted, globalPaused, isPausedByMainAudio, isReady, attemptAutoplay]);

  // ✅ NEW: Listen for GlobalAudioManager cleanup events and protect background music
  useEffect(() => {
    const handleGlobalCleanup = () => {
      // If background music was playing and got stopped by GlobalAudioManager, restart it
      if (isPlaying && audioRef.current && audioRef.current.paused && active && !globalPaused) {
        console.log('[BackgroundMusicPlayer] 🔄 Restarting after global cleanup');
        
        setTimeout(() => {
          if (audioRef.current && active && !globalPaused) {
            audioRef.current.play()
              .then(() => {
                setIsPlaying(true);
                console.log('[BackgroundMusicPlayer] ✅ Restarted successfully');
              })
              .catch(err => {
                console.log('[BackgroundMusicPlayer] ⚠️ Restart failed:', err);
                setIsPlaying(false);
              });
          }
        }, 500); // Short delay after cleanup
      }
    };

    // Listen for various cleanup events
    window.addEventListener('forceAudioCleanup', handleGlobalCleanup);
    
    // Also check periodically if background music needs to be restored
    const checkInterval = setInterval(() => {
      if (isPlaying && audioRef.current && audioRef.current.paused && active && !globalPaused && !isPausedByMainAudio) {
        console.log('[BackgroundMusicPlayer] 🔄 Auto-recovery: restarting paused background music');
        audioRef.current.play()
          .then(() => {
            console.log('[BackgroundMusicPlayer] ✅ Auto-recovery successful');
          })
          .catch(err => {
            console.log('[BackgroundMusicPlayer] ⚠️ Auto-recovery failed:', err);
            setIsPlaying(false);
          });
      }
    }, 3000); // Check every 3 seconds

    return () => {
      window.removeEventListener('forceAudioCleanup', handleGlobalCleanup);
      clearInterval(checkInterval);
    };
  }, [isPlaying, active, globalPaused, isPausedByMainAudio]);

  // Handle track ended
  const handleTrackEnded = () => {
    console.log('[BackgroundMusicPlayer] 🔄 Track ended, playing next');
    setIsPlaying(false);
    playNextTrack();
  };

  // Update volume when prop changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, []);

  if (!currentTrack) return null;

  return (
    <audio
      ref={audioRef}
      src={currentTrack}
      loop={false} // Không loop, sẽ chuyển bài khi hết
      onEnded={handleTrackEnded}
      style={{ display: 'none' }} // Ẩn hoàn toàn
      preload="none" // Low priority loading - only load when needed
      data-background-music="true" // Mark as background music
      data-continuous-playback="true" // Mark for continuous playback
    />
  );
};

export default BackgroundMusicPlayer; 